/**
 * plannerUtils.js — pure helpers for the Shift Planner tab of ShiftDashboard:
 * the double-booking check and the WhatsApp schedule text. No Firebase here, so
 * they're unit-tested directly (plannerUtils.test.js).
 */

const BRAND = 'Desi Bites';

export function toMin(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function fmt12(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')}${h >= 12 ? 'pm' : 'am'}`;
}

function hoursOf(s) {
  return Math.max(0, (toMin(s.endTime) - toMin(s.startTime)) / 60);
}

function fmtHours(h) {
  return `${Number.isInteger(h) ? h : h.toFixed(1)}h`;
}

function fmtDate(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "Oct 19 – 25", "Oct 26 – Nov 1"; the year is added only when it isn't this year. */
export function weekRangeLabel(weekStart, today = new Date()) {
  const s = new Date(weekStart + 'T00:00:00');
  const e = new Date(s); e.setDate(e.getDate() + 6);
  const mon = d => d.toLocaleDateString('en-US', { month: 'short' });
  const out = s.getMonth() === e.getMonth()
    ? `${mon(s)} ${s.getDate()} – ${e.getDate()}`
    : `${mon(s)} ${s.getDate()} – ${mon(e)} ${e.getDate()}`;
  return s.getFullYear() === today.getFullYear() && e.getFullYear() === today.getFullYear()
    ? out : `${out}, ${e.getFullYear()}`;
}

/** Where a week sits relative to this one: "This week", "Next week", "In 3 weeks", "2 weeks ago". */
export function weekRelLabel(weekStart, thisWeekStart) {
  const n = Math.round((new Date(weekStart + 'T00:00:00') - new Date(thisWeekStart + 'T00:00:00')) / (7 * 864e5));
  if (n === 0) return 'This week';
  if (n === 1) return 'Next week';
  if (n === -1) return 'Last week';
  return n > 0 ? `In ${n} weeks` : `${-n} weeks ago`;
}

const live = s => s.status !== 'cancelled';
const byStart = (a, b) => a.startTime.localeCompare(b.startTime);

/**
 * The shift that would double-book this person: a live shift of the same person on
 * the same day with the exact same times ('duplicate') or overlapping hours
 * ('overlap'). Back-to-back shifts (one ends 2pm, next starts 2pm) are fine.
 * `ignoreId` is the shift being edited. Returns { shift, kind } or null.
 */
export function findShiftClash(shifts, { userId, date, startTime, endTime, ignoreId }) {
  const mine = shifts.filter(p => live(p) && (ignoreId == null || p.id !== ignoreId)
    && p.userId === userId && p.date === date);
  const dup = mine.find(p => p.startTime === startTime && p.endTime === endTime);
  if (dup) return { shift: dup, kind: 'duplicate' };
  const s = toMin(startTime), e = toMin(endTime);
  const over = mine.find(p => s < toMin(p.endTime) && toMin(p.startTime) < e);
  return over ? { shift: over, kind: 'overlap' } : null;
}

/** Staff in the team's saved order (👥 Team); anyone not placed yet goes after, A–Z. */
export function orderStaff(staff, order = []) {
  const pos = new Map(order.map((uid, i) => [uid, i]));
  const name = s => s.name || s.email || '';
  return [...staff].sort((a, b) => {
    const pa = pos.has(a.uid) ? pos.get(a.uid) : Infinity;
    const pb = pos.has(b.uid) ? pos.get(b.uid) : Infinity;
    return pa !== pb ? pa - pb : name(a).localeCompare(name(b));
  });
}

/** Planned shifts that haven't started yet — later days, or later today. */
export function upcomingShifts(shifts, now = new Date()) {
  const pad = n => String(n).padStart(2, '0');
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return shifts
    .filter(p => p.status !== 'cancelled' && (p.date > today || (p.date === today && p.startTime > time)))
    .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime));
}

/**
 * The shifts a "Clear week" deletes. scope 'view' limits it to `inViewIds` (the people the
 * planner is showing); `keepPast` leaves days before `today` alone (their plan is history).
 */
export function clearWeekTargets(shifts, { scope, keepPast, inViewIds, today }) {
  return shifts.filter(p => (scope === 'all' || inViewIds.has(p.userId)) && !(keepPast && p.date < today));
}

/**
 * A staff phone number as WhatsApp wants it (country code + digits, no '+').
 * Bare 10-digit numbers are US numbers (the store is in the US). Anything we can't
 * read confidently returns '' so WhatsApp opens its chat picker instead of a wrong chat.
 */
export function waNumber(phone) {
  const raw = String(phone || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) return digits.length >= 8 ? digits : '';
  if (digits.length === 10) return `1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return digits;
  return '';
}

// Straight to WhatsApp's send page: the wa.me short link redirects there but mangles
// 4-byte emoji on the way (📅 arrives as �).
export function whatsappUrl(text, phone) {
  const n = waNumber(phone);
  return `https://api.whatsapp.com/send?${n ? `phone=${n}&` : ''}text=${encodeURIComponent(text)}`;
}

// Positions only matter in the message when the week mixes them (all-Cook is noise).
function positionLabeler(shifts) {
  const mixed = new Set(shifts.map(s => s.position || 'Cook')).size > 1;
  return s => (mixed && s.position ? ` (${s.position})` : '');
}

const empName = emp => emp.name || emp.email || 'Staff';

/**
 * The whole week as one message for the staff WhatsApp group.
 *  layout 'person' — each person, then their working days (staff find their own name once)
 *  layout 'day'    — each day, then each time slot and who works it (coverage at a glance)
 * `staff` is who to include, in display order; `planMap` is the planner's
 * `${uid}|${date}` → shifts map; `dayNames` lines up with `weekDays` (Mon…Sun).
 */
export function weekScheduleText({ weekDays, staff, planMap, dayNames, layout = 'person' }) {
  const shiftsOf = (emp, date) => (planMap[`${emp.uid}|${date}`] || []).filter(live).sort(byStart);
  const all = staff.flatMap(emp => weekDays.flatMap(date => shiftsOf(emp, date)));
  const pos = positionLabeler(all);
  const people = staff.filter(emp => weekDays.some(date => shiftsOf(emp, date).length)).length;
  const time = s => `${fmt12(s.startTime)}–${fmt12(s.endTime)}`;
  const dayLabel = i => `${dayNames[i]}, ${fmtDate(weekDays[i])}`;

  let out = `📅 *${BRAND} — Weekly Schedule*\n${dayLabel(0)} – ${dayLabel(weekDays.length - 1)}\n`;
  if (layout === 'day') {
    weekDays.forEach((date, i) => {
      out += `\n*${dayNames[i].toUpperCase()} · ${fmtDate(date)}*\n`;
      const slots = new Map(); // 'start|end' → { s, names }
      staff.forEach(emp => shiftsOf(emp, date).forEach(s => {
        const key = `${s.startTime}|${s.endTime}`;
        if (!slots.has(key)) slots.set(key, { s, names: [] });
        slots.get(key).names.push(`${empName(emp)}${pos(s)}`);
      }));
      if (!slots.size) { out += '_No shifts_\n'; return; }
      [...slots.values()]
        .sort((a, b) => byStart(a.s, b.s) || a.s.endTime.localeCompare(b.s.endTime))
        .forEach(({ s, names }) => { out += `• ${time(s)} — ${names.sort((a, b) => a.localeCompare(b)).join(', ')}\n`; });
    });
  } else {
    staff.forEach(emp => {
      const days = weekDays.map((date, i) => ({ i, shifts: shiftsOf(emp, date) })).filter(d => d.shifts.length);
      if (!days.length) return;
      out += `\n*${empName(emp)}*\n`;
      days.forEach(({ i, shifts }) => {
        out += `• ${dayLabel(i)} — ${shifts.map(s => `${time(s)}${pos(s)}`).join(', ')}\n`;
      });
    });
    if (!people) out += '\n_No shifts_\n';
  }
  out += `\n👥 ${people} ${people === 1 ? 'person' : 'people'} · ${all.length} ${all.length === 1 ? 'shift' : 'shifts'}`;
  return out;
}

/** One person's week, every day listed (days off say "Off") — for a direct message. */
export function personScheduleText({ emp, weekDays, planMap, dayNames }) {
  const days = weekDays.map(date => (planMap[`${emp.uid}|${date}`] || []).filter(live).sort(byStart));
  const all = days.flat();
  const pos = positionLabeler(all);
  const first = empName(emp).split(' ')[0];
  const total = all.reduce((h, s) => h + hoursOf(s), 0);

  let out = `Hi ${first} 👋 Your shifts at ${BRAND} for ${fmtDate(weekDays[0])} – ${fmtDate(weekDays[weekDays.length - 1])}:\n\n`;
  weekDays.forEach((date, i) => {
    const label = `*${dayNames[i]}, ${fmtDate(date)}*`;
    if (!days[i].length) { out += `${label} · Off\n`; return; }
    const shifts = days[i].map(s =>
      `${fmt12(s.startTime)}–${fmt12(s.endTime)}${pos(s)}${s.notes ? ` — _${s.notes.replace(/\s+/g, ' ').trim()}_` : ''}`);
    out += `${label} · ${shifts.join(', ')}\n`;
  });
  out += `\nTotal: ${fmtHours(total)} this week`;
  return out;
}
